// Phase 4 commands: handovers, partner updates, Prime pieces and supplier bills (04 §4.5b, 12).
import { z } from 'zod';
import { defineCommand } from '../define';
import { trackingNoSchema, unitIdSchema } from '../cargo';
import { vatInputSchema } from '../finance';
import { moneyLinkSchema } from '../money';
import { FAILED_REASONS, PARTNER_STATUSES } from '../partners';

const minor = z.number().int().nonnegative().max(1e12);
const units = z.array(unitIdSchema).min(1).max(200);
const id = z.string().min(1).max(40);
const at = z.iso.datetime({ offset: true });
const path = z.string().max(200);

export const openHandover = defineCommand({
  type: 'openHandover',
  permission: 'partner.handover',
  principals: [],
  offline: true,
  payload: z
    .object({ handoverId: z.string().regex(/^hnd_[0-9A-HJKMNP-TV-Z]{26}$/), partnerId: id })
    .strict(),
});
export const loadHandover = defineCommand({
  type: 'loadHandover',
  permission: 'partner.handover',
  principals: [],
  offline: true,
  payload: z
    .object({
      handoverId: id,
      unitIds: units,
      conditionNotes: z.record(z.string(), z.string().max(300)).default({}),
    })
    .strict(),
});
export const removeFromHandover = defineCommand({
  type: 'removeFromHandover',
  permission: 'partner.handover',
  principals: [],
  offline: true,
  payload: z.object({ handoverId: id, unitIds: units }).strict(),
});
export const handoverToPartner = defineCommand({
  type: 'handoverToPartner',
  permission: 'partner.handover',
  principals: [],
  offline: true,
  payload: z
    .object({
      handoverId: id,
      representativeName: z.string().trim().min(1).max(80),
      plate: z.string().max(20).default(''),
      signaturePath: path.default(''),
      vehiclePhotoPath: path.default(''),
      /** The count the partner's representative agreed to. Must match the loaded units. */
      agreedCount: z.number().int().positive().max(5000),
    })
    .strict(),
});
export const recordPartnerRef = defineCommand({
  type: 'recordPartnerRef',
  permission: 'partner.update',
  principals: [],
  offline: true,
  payload: z
    .object({
      refs: z
        .array(
          z
            .object({
              unitId: unitIdSchema.optional(),
              trackingNo: trackingNoSchema.optional(),
              ref: z.string().trim().min(1).max(60),
            })
            .strict()
            .refine(
              (r) => r.unitId !== undefined || r.trackingNo !== undefined,
              'unitId or trackingNo',
            ),
        )
        .min(1)
        .max(200),
      partnerId: id,
    })
    .strict(),
});
export const partnerUpdate = defineCommand({
  type: 'partnerUpdate',
  permission: 'partner.update',
  principals: ['partner', 'adapter'],
  offline: true,
  payload: z
    .object({
      handoverId: id,
      unitIds: units,
      status: z.enum(PARTNER_STATUSES),
      at,
      source: z.enum(['console', 'link', 'import', 'api']),
      receiverName: z.string().max(80).default(''),
      deliveryCode: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
      podPaths: z.array(path).max(5).default([]),
      collectedMinor: minor.optional(),
      reason: z.enum(FAILED_REASONS).optional(),
      note: z.string().max(500).default(''),
      original: z.string().max(4000).default(''),
    })
    .strict(),
});
export const returnFromPartner = defineCommand({
  type: 'returnFromPartner',
  permission: 'trip.unload',
  principals: [],
  offline: true,
  payload: z
    .object({ handoverId: id, unitIds: units, note: z.string().max(300).default('') })
    .strict(),
});
export const closeHandover = defineCommand({
  type: 'closeHandover',
  permission: 'partner.handover',
  principals: [],
  offline: false,
  payload: z.object({ handoverId: id, reason: z.string().trim().min(3).max(300) }).strict(),
});
export const chasePartner = defineCommand({
  type: 'chasePartner',
  permission: 'partner.update',
  principals: [],
  offline: false,
  payload: z
    .object({
      handoverId: id,
      unitIds: z.array(unitIdSchema).max(200).default([]),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

// ---- Bills (12 §12.6, 14) --------------------------------------------------------

const billLine = z
  .object({
    unitId: unitIdSchema.optional(),
    trackingNo: trackingNoSchema.optional(),
    description: z.string().max(120).default(''),
    amountMinor: minor,
    weightKg: z.number().positive().max(1000).optional(),
  })
  .strict();

export const recordBill = defineCommand({
  type: 'recordBill',
  permission: 'finance.bill.record',
  principals: [],
  offline: false,
  payload: z
    .object({
      billId: z.string().regex(/^bil_[0-9A-HJKMNP-TV-Z]{26}$/),
      partnerId: id.optional(),
      supplierName: z.string().trim().min(1).max(120),
      billNo: z.string().trim().min(1).max(40),
      billDate: z.iso.date(),
      dueDate: z.iso.date(),
      totalMinor: minor,
      lines: z.array(billLine).max(2000).default([]),
      category: z.string().max(40).optional(),
      /** The supplier's VAT inside `totalMinor` (06 §6.3): posted to 1040 VAT input, apart from the cost. */
      vat: vatInputSchema.optional(),
      link: moneyLinkSchema.optional(),
      note: z.string().max(300).default(''),
      filePath: path.optional(),
    })
    .strict(),
});
export const payBill = defineCommand({
  type: 'payBill',
  permission: 'finance.bill.pay',
  principals: [],
  offline: false,
  payload: z
    .object({
      billId: id,
      walletId: id,
      /** Cash paid from the wallet. May be 0 when the bill is settled by `offsetCollectionsMinor` alone (both 0: `nothing_to_pay`). */
      amountMinor: minor,
      /**
       * Part of the bill settled by cash the partner already collected for us (Dr 2000, Cr 1003, 12 §12.6).
       * Only for a bill of a partner; at most what the partner holds and what the bill still owes.
       */
      offsetCollectionsMinor: minor.default(0),
      reference: z.string().max(60).default(''),
    })
    .strict(),
});

export const PARTNER_COMMANDS = {
  openHandover,
  loadHandover,
  removeFromHandover,
  handoverToPartner,
  recordPartnerRef,
  partnerUpdate,
  returnFromPartner,
  closeHandover,
  chasePartner,
  recordBill,
  payBill,
} as const;
